import React, { FC, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import MaterialReactTable, { MRT_RowSelectionState } from "material-react-table";
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, TextField } from "@mui/material";
import { Dayjs } from "dayjs";

import { ApprovalsRequestBody, createApprovalBillData, createApprovalLock, createApprovalSignOff, createLockedBillData } from "api";
import { CloseButton, EsgDatePickerField, InputFieldWrapper, Label14Medium, Label16Medium, PrimaryLabel } from "components";
import { useFilteredSitesMultiselect, useNotify } from "hooks";

import { AddSiteModalProps } from "./types";
import { APPROVALS_MESSAGES } from "../consts";
import { ADD_MODAL_COLUMNS } from "../columns";

export const ApprovalsAddModal: FC<AddSiteModalProps> = ({ isOpen, onClose, status }) => {
  // ---- Hooks ----
  const notify = useNotify();

  // ---- State ----
  const [startDateValue, setStartDateValue] = useState<Dayjs | null>(null);
  const [endDateValue, setEndDateValue] = useState<Dayjs | null>(null);
  const [step, setStep] = useState<number>(1);
  const [confirmartionData, setConfirmartionData] = useState<any>([]);
  const [key, setKey] = useState<number>(0);
  const [signatureValue, setSignatureValue] = useState<string>("");
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  // ---- Const ----
  const totalSteps = 2;

  // ---- API Calls ----
  const { data: approvalData, mutateAsync: postApprovalBillData } = useMutation(createApprovalBillData);
  const { data: lockedData, mutateAsync: postLockedBillData } = useMutation(createLockedBillData);
  const { mutateAsync: approvalDataMutate, isLoading: isApprovalLoading } = useMutation(createApprovalSignOff);
  const { mutateAsync: lockDataMutate, isLoading: isLockedLoading } = useMutation(createApprovalLock);

  const { sitesOptions } = useFilteredSitesMultiselect({});

  const selectedRow = Object.keys(rowSelection);

  const siteIds = selectedRow.map(id => Number(id));

  // ---- Functions ----
  const handleClose = () => {
    setStep(1);
    onClose();
    setStartDateValue(null);
    setEndDateValue(null);
    setRowSelection({});
    setSignatureValue("");
  };

  const handleNextButtonClick = async () => {
    if (sitesOptions.length > 0 && siteIds.length === 0) {
      notify.error("Please select at least one site");
      return;
    };

    if (!startDateValue?.isValid() || !endDateValue?.isValid() || startDateValue === null || endDateValue === null) {
      notify.error("Please select a valid start and end date");
      return;
    };

    if (startDateValue.isAfter(endDateValue) || endDateValue.isBefore(startDateValue)) {
      notify.error("Start date must be before end date");
      return;
    };

    if (startDateValue && endDateValue) {
      if (status === "signoff") {
        await postApprovalBillData({
          startDate: startDateValue?.format("YYYY-MM"),
          endDate: endDateValue?.format("YYYY-MM"),
          siteIds,
        }, {
          onError: (e: any) => notify.error(e?.response?.data ?? "Something went wrong!"),
          onSuccess: () => {
            setStep(2)
          },
        })
      }
      if (status === "lock") {
        await postLockedBillData({
          startDate: startDateValue?.format("YYYY-MM"),
          endDate: endDateValue?.format("YYYY-MM"),
          siteIds,
        }, {
          onError: (e: any) => notify.error(e?.response?.data ?? "Something went wrong!"),
          onSuccess: () => {
            setStep(2)
          },
        })
      }
    }
  };

  const handleConfirmButtonClick = () => {
    const payload = {
      startDate: startDateValue?.format("YYYY-MM"),
      endDate: endDateValue?.format("YYYY-MM"),
      siteIds,
      userSign: signatureValue
    }

    if (status === "signoff") {
      approvalDataMutate(payload as ApprovalsRequestBody, {
        onError: (e: any) => {
          notify.error(e?.response?.data ?? APPROVALS_MESSAGES.SIGN_OFF_ERROR)
        },
        onSuccess: () => {
          notify.success(APPROVALS_MESSAGES.SIGN_OFF_SUCCESS);
          handleClose();
        },
      })
    }

    if (status === "lock") {
      lockDataMutate(payload as ApprovalsRequestBody, {
        onError: (e: any) => {
          notify.error(e?.response?.data ?? APPROVALS_MESSAGES.LOCK_ERROR)
        },
        onSuccess: () => {
          notify.success(APPROVALS_MESSAGES.LOCK_SUCCESS);
          handleClose();
        },
      })
    }
  };

  // ---- Effects ----
  useEffect(() => {
    if (approvalData) {
      setConfirmartionData(approvalData)
    }
    if (lockedData) {
      setConfirmartionData(lockedData)
    }
  }, [approvalData, lockedData]);

  useEffect(() => {
    setKey((prev) => prev + 1);
  }, []);

  return (
    <Dialog open={isOpen} onClose={handleClose} PaperProps={{ sx: { width: "680px" } }}>
      <DialogTitle sx={{ padding: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer', py: "12px", px: "24px", alignItems: 'center' }}>
          <PrimaryLabel sx={{ color: 'black' }}>Step {step}/{totalSteps}</PrimaryLabel>
          <Box onClick={() => handleClose()}>
            <CloseButton />
          </Box>
        </Box>
        <Divider sx={{ width: '100%', mb: "12px" }} />
      </DialogTitle>
      <DialogContent>
        {step === 1 ?
          <Box>
            {status === "signoff" || status === "undosignoff" ? <Label16Medium>
              Please select the sites you are confirming. Data providers will no longer be able to make changes to the data points for this period.
            </Label16Medium> : (
              <Label16Medium>
                Please select the sites you are locking.
              </Label16Medium>
            )}
            <Box>
              {sitesOptions && <MaterialReactTable
                key={key}
                enableTopToolbar
                enableRowVirtualization
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                enableRowSelection
                enableSelectAll
                data={sitesOptions}
                enablePagination={false}
                columns={ADD_MODAL_COLUMNS}
                enableStickyHeader
                enableColumnFilters
                enableColumnResizing={false}
                enableColumnDragging={false}
                layoutMode="grid"
                initialState={{ density: 'compact' }}
                muiTableContainerProps={{
                  sx: {
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "transparent"
                    },

                    "&::-webkit-scrollbar": {
                      right: "0",
                      width: "8px",
                      height: "12px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#D9D9D9",
                      marginTop: "40px",
                      borderRadius: "8px",
                      width: "3px",
                      height: "40px"
                    },
                    "& thead tr:first-of-type th:first-of-type": {
                      paddingLeft: "20px",
                    },
                    "& tbody tr td:first-of-type": {
                      paddingLeft: "20px",
                    },
                    "& tbody tr:first-of-type td > p": {
                      display: "flex",
                      width: "100%",
                      justifyContent: "center",
                    },
                    height: "200px",
                    border: "1px solid #D9D9D9",
                    borderRadius: "6px",
                    my: 3
                  },
                }}
                muiTablePaperProps={{
                  sx: {
                    borderRadius: "10px",
                    boxShadow: "none",
                  },
                }}
                muiTopToolbarProps={{
                  sx: {
                    height: '1px',
                    minHeight: '1px',
                    backgroundColor: 'transparent',
                    '& .MuiIconButton-root': {
                      display: 'none',
                    },
                  },
                }}
                muiBottomToolbarProps={{
                  sx: {
                    display: "none",

                  },
                }}
                muiTableHeadCellProps={{
                  sx: {
                    lineHeight: "30px",
                    "& .MuiBadge-root": {
                      display: "none",
                    },
                  },
                }}
                muiTableHeadRowProps={{
                  sx: {
                    "& > th": {
                      paddingTop: 1,
                      paddingBottom: 1,
                    },
                    height: "auto",
                    backgroundColor: "white",
                  },
                }}
                muiTableBodyRowProps={{
                  sx: {
                    cursor: "pointer",
                    backgroundColor: "",
                  },
                }}
                getRowId={(row: any) => row.value}
                onRowSelectionChange={setRowSelection}
                state={{ rowSelection }}
              />}
            </Box>
            {(status === "signoff" || status === "lock") &&
              <Box>
                <Label16Medium>
                  Please enter the month and year for the data you are {status.includes("signoff") ? "confirming" : "locking"}:
                </Label16Medium>
                <Box sx={{ display: 'flex', gap: "24px" }}>
                  <Box sx={{ display: 'flex', my: 3, maxWidth: "200px", gap: "24px" }}>
                    <EsgDatePickerField
                      views={["year", "month"]}
                      placeholdervalue="MM/YYYY"
                      format="MM/YYYY"
                      openTo="year"
                      onAccept={(date: Dayjs | null) => setStartDateValue(date)}
                      onChange={(date: Dayjs | null) => setStartDateValue(date)}
                      value={startDateValue}
                      clearDate={false}
                      label="Start Date"
                      disableFuture
                      disableHighlightToday
                    />
                  </Box>
                  <Box sx={{ display: 'flex', my: 3, maxWidth: "200px", gap: "24px" }}>
                    <EsgDatePickerField
                      views={["year", "month"]}
                      placeholdervalue="MM/YYYY"
                      format="MM/YYYY"
                      openTo="year"
                      onAccept={(date: Dayjs | null) => setEndDateValue(date)}
                      onChange={(date: Dayjs | null) => setEndDateValue(date)}
                      value={endDateValue}
                      clearDate={false}
                      label="End Date"
                      disableFuture
                      disableHighlightToday
                    />
                  </Box>
                </Box>
              </Box>
            }
          </Box> : (
            <Box>
              <Label16Medium>
                Please write your name as a digital signature for the following:
              </Label16Medium>
              <Box sx={{ my: 3 }}>
                {confirmartionData?.data?.map((t: any) => <>
                  <Label16Medium>{t?.siteName}</Label16Medium>
                  <Label14Medium
                    sx={{
                      fontStyle: 'italic',
                      fontWeight: 300,
                      my: 1
                    }}>
                    <span style={{ color: "#60A034", fontWeight: 600 }}>
                      {t.statusCount}</span>
                    <span style={{ fontWeight: 600 }}>/{t.total}</span>
                    {`bills ready to be ${status === "signoff" ? "signed off" : "locked"}`}
                  </Label14Medium>
                </>
                )}
                {(confirmartionData?.data?.length === 0) &&
                  <Label16Medium sx={{ color: "#DD7615" }}>
                    Warning : There are no bills associated with this time period, would you like to proceed?
                  </Label16Medium>
                }
              </Box>
              <Box sx={{ display: 'flex', gap: 3, my: 3 }}>
                <InputFieldWrapper label="Type your name below to sign off">
                  <TextField
                    sx={{ width: "100%", borderRadius: "20px", borderColor: signatureValue ? "" : "red" }}
                    name="signOff"
                    value={signatureValue}
                    onChange={(e) => setSignatureValue(e.target.value)}
                    helperText=""
                    multiline
                    rows={3}
                  />

                </InputFieldWrapper>
              </Box>
            </Box>
          )}
        <Button
          onClick={() => {
            if (step === 1) {
              handleNextButtonClick();
            } else {
              handleConfirmButtonClick();
            }
          }}
          variant="contained"
          color="primary"
          sx={{ width: '150px', borderRadius: 2 }}
        >
          {(isLockedLoading || isApprovalLoading)
            ? (<CircularProgress size={24} color="inherit" />)
            : (step === 1 ? "Next" : "Confirm")}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
